import { useEffect, useState, useCallback } from "react";
import { Play, Radio, X, Minus, Activity } from "lucide-react";

import { ops, widget } from "@/lib/api";
import { fmtTime, shortId } from "@/lib/format";
import { ALL_CANVASES, GROUP_COLOR, STATE_COLOR, actionToGroup, nodeActivity } from "@/lib/pipelines";
import PipelineCanvas from "@/components/PipelineCanvas";

/* PJ1 OS — multi-pipeline live canvas (n8n-style).
 * Tabs: Master + one canvas per pipeline. One shared live feed
 * (agent_tasks + audit) lights every canvas. THE WALL: activity only. */

export default function Canvas() {
  const [status, setStatus] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [running, setRunning] = useState(false);
  const [activeId, setActiveId] = useState("master");
  const [sel, setSel] = useState(null);
  const [streamOpen, setStreamOpen] = useState(true);

  const def = ALL_CANVASES.find((c) => c.id === activeId) || ALL_CANVASES[0];

  const selTasks = sel ? tasks.filter((t) => (sel.agents || []).includes(t.agent)) : [];
  const selAudit = sel ? audit.filter((e) => (sel.actions || []).some((s) => (e.action || "").toLowerCase().includes(s))) : [];
  const selAct = sel ? nodeActivity(sel, tasks, audit, now) : null;

  const tick = useCallback(async () => {
    try {
      const [s, t, a] = await Promise.all([ops.hermesStatus(), ops.agentTasks(80), ops.audit({ limit: 80 })]);
      setStatus(s); setTasks(t); setAudit(a); setNow(Date.now());
    } catch {/* transient */}
  }, []);

  useEffect(() => {
    tick();
    const id = setInterval(tick, 3000);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(id); clearInterval(clock); };
  }, [tick]);

  const simulate = async () => {
    setRunning(true);
    try {
      const dep = new Date(Date.now() + 3 * 864e5).toISOString();
      await widget.quote({ legs: [{ from_airport: "KTEB", to_airport: "KMIA", departure_at: dep }], pax: 4, preferred_cabin: "midsize" });
      for (let i = 0; i < 6; i++) { await tick(); await new Promise((r) => setTimeout(r, 700)); }
    } catch {/* ignore */} finally { setRunning(false); }
  };

  const liveCount = def.nodes.filter((n) => nodeActivity(n, tasks, audit, now).hot).length;

  return (
    <div data-testid="page-canvas" className="relative" style={{ height: "100vh", overflow: "hidden",
      background: "radial-gradient(1100px 560px at 80% -8%, rgb(169 130 28 / 0.12), transparent 60%), radial-gradient(800px 480px at -5% 108%, rgb(158 92 44 / 0.10), transparent 55%), linear-gradient(180deg, rgb(244,239,230), rgb(236,229,216))" }}>
      <style>{`
        @keyframes pj-dash { to { stroke-dashoffset: -24; } }
        @keyframes pj-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(169,130,28,0);} 50% { box-shadow: 0 0 0 6px rgba(169,130,28,0.20);} }
        .pj-edge-hot { stroke-dasharray: 6 6; animation: pj-dash 0.7s linear infinite; }
        .pj-node-hot { animation: pj-pulse 1.4s ease-in-out infinite; }
        .pj-canvas-grid { background-image: radial-gradient(rgb(223,214,198) 1px, transparent 1px); background-size: 24px 24px; }
      `}</style>

      {/* Header */}
      <div className="absolute z-20 top-0 left-0 right-0 px-5 pt-3" style={{ pointerEvents: "none" }}>
        <div className="flex items-center justify-between gap-4" style={{ pointerEvents: "auto" }}>
          <div className="min-w-0">
            <div className="pj-label">· LIVE CANVAS · {def.name}</div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="mono text-[11px] flex items-center gap-1.5 px-2 py-1.5" style={{ color: "rgb(32,138,78)", border: "1px solid rgb(var(--ln))", borderRadius: 2 }}>
              <Radio size={11} className={liveCount ? "pj-node-hot" : ""} /> {liveCount} active · 3s
            </span>
            <button onClick={simulate} disabled={running} data-testid="canvas-simulate"
              className="flex items-center gap-2 mono text-[11px] uppercase tracking-[0.12em] px-3 py-2 hover:opacity-90 disabled:opacity-50" style={{ borderRadius: 2, background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}>
              <Play size={13} /> {running ? "Running…" : "Simulate"}
            </button>
          </div>
        </div>

        {/* Pipeline tabs */}
        <div className="flex flex-wrap gap-1.5 mt-2" style={{ pointerEvents: "auto" }}>
          {ALL_CANVASES.map((c) => {
            const on = c.id === activeId;
            return (
              <button key={c.id} onClick={() => { setActiveId(c.id); setSel(null); }} data-testid={`tab-${c.id}`}
                className="mono text-[10.5px] uppercase tracking-[0.08em] px-2.5 py-1.5 transition-colors"
                style={{
                  borderRadius: 3,
                  border: `1px solid ${on ? "rgb(196,156,64)" : "rgb(var(--ln))"}`,
                  background: on ? "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))" : "rgb(var(--surface))",
                  color: on ? "rgb(252,249,243)" : "rgb(var(--dim))",
                  fontWeight: on ? 600 : 400,
                }}>
                {c.id === "master" ? "★ Master" : c.name.split(" (")[0]}
              </button>
            );
          })}
        </div>
        <div className="mono text-[10.5px] mt-2 truncate" title={def.desc} style={{ color: "rgb(var(--dim))", pointerEvents: "auto", maxWidth: "78%" }}>{def.desc}</div>
      </div>

      {/* Counters */}
      <div className="absolute z-20 top-[118px] left-5 flex gap-px" style={{ background: "rgb(var(--ln))", borderRadius: 2, overflow: "hidden" }}>
        {[["running", "running"], ["queued", "queued"], ["succeeded", "succeeded"], ["waiting_human", "waiting"], ["failed", "failed"]].map(([k, lbl]) => (
          <div key={k} className="px-3 py-2" style={{ minWidth: 76, background: "rgb(var(--surface))" }}>
            <div className="pj-label" style={{ fontSize: 9 }}>{lbl}</div>
            <div className="mono text-[15px]" style={{ color: STATE_COLOR[k] || "rgb(var(--fg))" }}>{status?.[k] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* The active pipeline canvas (remounts per tab to reset pan/zoom) */}
      <PipelineCanvas key={def.id} def={def} tasks={tasks} audit={audit} now={now} onSelect={setSel} selectedId={sel?.id} />

      {/* Node detail drawer (when a node is selected) */}
      {sel && (
        <div className="absolute z-30 top-[118px] right-0 m-4" data-testid="node-detail"
          style={{ width: 340, maxHeight: "78vh", background: "rgba(252,249,243,0.97)", border: "1px solid rgb(var(--border-strong))", borderRadius: 6, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div className="px-4 py-3 flex items-start justify-between" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
            <div>
              <div className="font-display text-[18px] leading-none" style={{ color: "rgb(var(--fg))" }}>{sel.label}</div>
              <div className="mono text-[10.5px] mt-1.5" style={{ color: "rgb(var(--dim))" }}>{sel.role}</div>
              <div className="flex gap-1.5 mt-2">
                <span className="mono text-[8.5px] uppercase px-1.5 py-0.5" style={{ border: `1px solid ${GROUP_COLOR[sel.group] || "rgb(var(--border-strong))"}`, color: GROUP_COLOR[sel.group] || "rgb(var(--dim))", borderRadius: 2 }}>{sel.group}</span>
                <span className="mono text-[8.5px] uppercase px-1.5 py-0.5" style={{ border: "1px solid rgb(var(--border-strong))", color: "rgb(var(--dim))", borderRadius: 2 }}>{sel.kind || "agent"}</span>
                <span className="mono text-[8.5px] uppercase px-1.5 py-0.5" style={{ border: `1px solid ${STATE_COLOR[selAct?.state] || "rgb(var(--border-strong))"}`, color: STATE_COLOR[selAct?.state] || "rgb(var(--dim))", borderRadius: 2 }}>{selAct?.hot ? "● live" : selAct?.state || "idle"}</span>
              </div>
            </div>
            <button onClick={() => setSel(null)} className="p-1" style={{ color: "rgb(var(--dim))" }} aria-label="Close"><X size={16} /></button>
          </div>

          <div style={{ overflowY: "auto" }}>
            <div className="px-4 py-3" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
              <div className="pj-label mb-1.5">Lights from</div>
              <div className="flex flex-wrap gap-1">
                {(sel.agents || []).map((a) => <span key={a} className="mono text-[9px] px-1.5 py-0.5" style={{ background: "rgb(var(--surface-2))", color: "rgb(36,104,184)", borderRadius: 2 }}>agent:{a}</span>)}
                {(sel.actions || []).map((a) => <span key={a} className="mono text-[9px] px-1.5 py-0.5" style={{ background: "rgb(var(--surface-2))", color: "rgb(var(--dim))", borderRadius: 2 }}>{a}</span>)}
                {!(sel.agents || []).length && !(sel.actions || []).length && <span className="mono text-[10px]" style={{ color: "rgb(var(--muted-2))" }}>no live signals mapped</span>}
              </div>
            </div>

            <div className="px-4 py-3" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
              <div className="pj-label mb-2">Recent agent tasks ({selTasks.length})</div>
              {selTasks.slice(0, 8).map((t) => (
                <div key={t.id} className="mono text-[10px] flex items-center justify-between py-1">
                  <span style={{ color: STATE_COLOR[t.state] || "rgb(var(--dim))" }}>{t.state}</span>
                  <span style={{ color: "rgb(var(--dim))" }}>{fmtTime(t.created_at)}</span>
                </div>
              ))}
              {!selTasks.length && <div className="mono text-[10px]" style={{ color: "rgb(var(--muted-2))" }}>none yet</div>}
            </div>

            <div className="px-4 py-3">
              <div className="pj-label mb-2">Recent events ({selAudit.length})</div>
              {selAudit.slice(0, 14).map((e) => (
                <div key={e.id} className="mono text-[10px] py-1.5" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
                  <div className="truncate" style={{ color: "rgb(var(--fg))" }}>{e.action}</div>
                  <div style={{ fontSize: 9, color: "rgb(var(--dim))" }}>{e.entity_type} {shortId(e.entity_id)} · {fmtTime(e.created_at)}</div>
                </div>
              ))}
              {!selAudit.length && <div className="mono text-[10px]" style={{ color: "rgb(var(--muted-2))" }}>no matching events — click "Simulate" to generate activity</div>}
            </div>
          </div>
        </div>
      )}

      {/* Live event stream (collapsible; hidden while inspecting a node) */}
      {!sel && streamOpen && (
      <div className="absolute z-20 bottom-0 right-0 m-4" style={{ width: 320, maxHeight: "40vh", background: "rgba(252,249,243,0.96)", border: "1px solid rgb(var(--ln))", borderRadius: 4, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
          <span className="pj-label">Live event stream</span>
          <div className="flex items-center gap-2">
            <span className="mono text-[10px]" style={{ color: "rgb(var(--dim))" }}>{audit.length}</span>
            <button onClick={() => setStreamOpen(false)} aria-label="Minimize" title="Minimize" className="hover:opacity-70" style={{ color: "rgb(var(--dim))" }}><Minus size={14} /></button>
          </div>
        </div>
        <div style={{ overflowY: "auto" }}>
          {audit.slice(0, 40).map((e) => {
            const g = actionToGroup(e.action);
            return (
              <div key={e.id} className="px-3 py-2 mono text-[10.5px] flex items-start gap-2" style={{ borderBottom: "1px solid rgb(var(--ln))" }}>
                <span style={{ width: 6, height: 6, borderRadius: 6, marginTop: 4, flex: "0 0 auto", background: g ? GROUP_COLOR[g] : "rgb(var(--border-strong))" }} />
                <div className="min-w-0">
                  <div className="truncate" style={{ color: "rgb(var(--fg))" }}>{e.action}</div>
                  <div style={{ fontSize: 9.5, color: "rgb(var(--dim))" }}>{e.entity_type} {shortId(e.entity_id)} · {fmtTime(e.created_at)}</div>
                </div>
              </div>
            );
          })}
          {audit.length === 0 && <div className="px-3 py-6 mono text-[11px]" style={{ color: "rgb(var(--dim))" }}>No events yet — click "Simulate".</div>}
        </div>
      </div>
      )}

      {/* Re-open pill when minimized */}
      {!sel && !streamOpen && (
        <button onClick={() => setStreamOpen(true)} data-testid="stream-reopen"
          className="absolute z-20 bottom-0 right-0 m-4 flex items-center gap-2 mono text-[11px] px-3 py-2 hover:opacity-90"
          style={{ background: "rgba(252,249,243,0.97)", border: "1px solid rgb(var(--ln))", borderRadius: 6, color: "rgb(var(--fg))", boxShadow: "0 6px 20px rgb(120 92 30 / 0.14)" }}>
          <Activity size={13} style={{ color: "rgb(32,138,78)" }} /> Events
          <span style={{ color: "rgb(var(--dim))" }}>{audit.length}</span>
        </button>
      )}
    </div>
  );
}
